import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAuthorization } from '$/components/core/Authentication/hooks/useAuthorization';
import {
  useDesignGuideStore,
  type HarmonyFacet,
} from '$/pages/DashboardPages/pages/DesignGuide/stores/useDesignGuideStore';
import { type Harmony } from '$/services/usecases/materials/mapper/harmonies';
import { harmoniesQuery } from '$/services/usecases/materials/queries';
import {
  applyFacetFilters,
  computeFacetCounts,
  type FacetConfig,
} from '$/utils/facetFilter';

export const HARMONY_FACETS: Record<HarmonyFacet, FacetConfig<Harmony>> = {
  scene: { mode: 'single', accessor: (h) => h.scene },
  mood: { mode: 'single', accessor: (h) => h.mood },
  tonality: { mode: 'single', accessor: (h) => h.tonality },
  sceneType: { mode: 'single', accessor: (h) => h.sceneType },
  colorRange: { mode: 'multi', accessor: (h) => h.colorRange },
};

export const useFilteredHarmonies = () => {
  const { hasPermission } = useAuthorization();

  const { data, isLoading } = useQuery(
    harmoniesQuery({ enabled: hasPermission('DesignGuide_Open') }),
  );

  const selection = useDesignGuideStore.useSelection();

  return useMemo(() => {
    const all = data?.isSuccessful ? data.response.payload : [];
    const items = applyFacetFilters(all, selection, HARMONY_FACETS).sort(
      (a, b) => a.position - b.position,
    );

    return {
      items,
      facetCounts: computeFacetCounts(all, selection, HARMONY_FACETS),
      hasPermission: hasPermission('DesignGuide_Open'),
      isLoading,
    };
  }, [data, selection, isLoading, hasPermission]);
};
